import {
  Box,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useTranslation } from "react-i18next";
import { OpeningHour } from "../../../types/clients";

const formatTime = (time?: string) => {
  if (!time) {
    return "";
  }
  return time.split(":").slice(0, 2).join(":");
};

type AddressAccordionData = {
  opening_hours?: OpeningHour[];
  country?: string;
  city?: string;
  street?: string;
  zip_code?: string;
  phone_number?: string;
  email?: string;
  notes?: string;
};

interface ClientAddressAccordionProps {
  address: AddressAccordionData;
  defaultExpanded?: boolean;
  title?: string;
}

export const AddressAccordeon = ({
  address,
  defaultExpanded = false,
  title,
}: ClientAddressAccordionProps) => {
  const { t } = useTranslation();

  const buildGoogleMapsUrl = (addr: AddressAccordionData): string => {
    const addressParts = [addr.street, addr.city, addr.zip_code, addr.country]
      .filter(Boolean)
      .join(", ");

    if (!addressParts) {
      return "";
    }

    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressParts)}`;
  };

  const googleMapsUrl = buildGoogleMapsUrl(address);

  const dayNames: { [key: string]: string } = {
    "1": t("Monday"),
    "2": t("Tuesday"),
    "3": t("Wednesday"),
    "4": t("Thursday"),
    "5": t("Friday"),
    "6": t("Saturday"),
    "7": t("Sunday"),
  };

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      variant="outlined"
      disableGutters
      sx={{ maxWidth: 720, borderColor: "divider", "&:before": { display: "none" } }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ "& .MuiAccordionSummary-content": { width: "100%", mr: 1 } }}
      >
        <Box sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {title || `${t("Address")} & ${t("Opening Hours")}`}
          </Typography>
          {googleMapsUrl && (
            <Button
              href={googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              startIcon={<OpenInNewIcon fontSize="small" />}
              sx={{ px: 1, minWidth: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {t("View on Google Maps")}
            </Button>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <TableContainer component={Paper} variant="outlined" sx={{ border: 0 }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: "35%" }}>{t("Street")}</TableCell>
                <TableCell>{address.street || "-"}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t("City")}</TableCell>
                <TableCell>
                  {[address.city, address.zip_code].filter(Boolean).join(" ") || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t("Phone Number")}</TableCell>
                <TableCell>{address.phone_number || "-"}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t("Email")}</TableCell>
                <TableCell>{address.email || "-"}</TableCell>
              </TableRow>
              {address.notes && (
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t("Notes")}</TableCell>
                  <TableCell sx={{ whiteSpace: "pre-wrap" }}>{address.notes}</TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t("Opening Hours")}</TableCell>
                <TableCell>
                  {Array.isArray(address.opening_hours) && address.opening_hours.length > 0 ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      {address.opening_hours.map((oh, idx) => (
                        <Typography
                          key={oh.id || idx}
                          variant="body2"
                          sx={{ color: "text.secondary" }}
                        >
                          {`${dayNames[oh.week_day] || oh.week_day}: ${formatTime(oh.from_hour)} - ${formatTime(oh.to_hour)}`}
                        </Typography>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                      {t("No opening hours")}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  );
};
